import { ExtraPnlColumnsEnum } from "types/enums";
import { DataDumpAggregateType } from "types/types";

/** Configuration for a PowerBI report link */
export type PowerBIReport = {
  /** PowerBI report ID */
  id: string;
  /** Display name for the report link */
  name: string;
};

export type GeneralConfigType = {
  /** whitelabel configs e.g. to swap Raven logo for another */
  whitelabel: {
    logoUrl?: string;
    logoWidth?: number;
    logoHeight?: number;
  };
  /** enable reports & access via the left drawer menu button */
  reportsEnabled: boolean;
  /** enable Operator page via the left drawer menu button
   * - true: enable for all processes
   * - false: disable for all processes
   * - string[]: enable for specific processes e.g. ["raveng_d_001","raveng_d_002"]
   */
  operatorPageEnabled: boolean | string[];
  /** enable Operator Name in Expanded Process Tile */
  expandedProcessTileOperatorName: boolean;
  /** enable SkuId in Expanded Process Tile */
  expandedProcessTileSkuId: boolean;
  /** enable SkuName in Expanded Process Tile */
  expandedProcessTileSkuName: boolean;
  /** enable Count in Expanded Process Tile */
  expandedProcessTileCount: boolean;
  /** enable TeamSize in Expanded Process Tile */
  expandedProcessTileTeamSize: boolean;
  /** for Count in Expanded Process Tile, show a different dataDumpKey */
  expandedProcessTileCountCustomKey: keyof DataDumpAggregateType | null;
  /** enable Ahead/Behind module in Expanded Process Tile for a list of process ids e.g. ["raveng_d_001","raveng_d_002"] or ["ALL"] for all processes */
  expandedProcessTileAheadBehindProcesses: string[];
  /** multiplier for the width of the process tile and group tile */
  tileWidthMultiplier: number;
  /** enable Top Losses module in Expanded Process Tile */
  expandedProcessTileTopLosses: boolean;
  /** allow access to the Trends page via the left drawer */
  accessToTrendsPage: boolean;
  /** maximum number of days you can request at once */
  trendsPageMaxQueryRangeDays: number;
  /** Auto-redirect from the Group Tiles page into the first group's Process Tiles page (typically this is done when the customer only has one group, so the Group Tiles page is redundant) */
  autoRedirectIntoFirstGroup: boolean;
  /** customers who prefer to show metrics for only their constraint process on the tiles page of their "all" group */
  isAllGroupSerial: boolean;
  /** clicking a Process Tile will direct these customers to the Grid page instead of the Hublet View page */
  redirectFromHubletViewPageToGridPage: boolean;
  /** enable the MachineBreakdownChart */
  enableMachineBreakdownChart: boolean;
  /** enable the secondary PnL chart that shows non-leader processes in leader/follower config situation */
  enableSecondaryProcessesPnlChart: boolean;
  /** Enable retrotagging UI in Flight to be visible */
  retrotaggingEnabled: boolean;
  /** in the Lot Info Module, for "Forecasted Lot Completion", use the target rate instead of the current rate (e.g. for customers who don't have a current rate) */
  lotInfoModuleUseTargetRateInsteadOfCurrentRate: boolean;
  /** enable Lot Info Module on the Grid page */
  enableLotInfoModule: boolean;
  /** shows the Sku Timeline below the timeline on Hublet Page (when in expanded timeline state) */
  showSkuTimeline: boolean;
  /** shows the Count Timeline below the timeline on Hublet Page (when in expanded timeline state) */
  showCountTimeline: boolean;
  /** shows the Reject Count Timeline below the timeline on Hublet Page (when in expanded timeline state) */
  showRejectCountTimeline: boolean;
  /** shows the Operator Timeline below the timeline on Hublet Page (when in expanded timeline state) */
  showOperatorTimeline: boolean;
  /** on Trends page, show the HighlightTagsSelector and highlighted tags below the timelines */
  trendsPageShowTagHighlight: boolean;
  /** on Timeline page, show the HighlightTagsSelector and highlighted tags below the timelines */
  timelinePageShowTagHighlight: boolean;
  /** override maxDataDumpDuration in DataDumpGetter > useIsTimeRangeSmallEnough function */
  overrideDdaLimitDays: number;
  /** fetch timelineDataDumpRows in DataGetter & display in TimelineTooltip > More Info tab */
  timelineTooltipShowCounts: boolean;
  /** show head count (team size) in Flight AheadBehindSummaryCharts */
  aheadBehindShowHeadCount: boolean;
  /** scale target rate by some percent (e.g. 0.5 = 50%) in order to change chart red to green at a lower output than target (per-process, or use "ALL" for all processes) */
  aheadBehindScaleTargetRate: {
    [processId: string]: number;
  };
  /** same as aheadBehindScaleTargetRate but only changes the color of the chart, not the output loss sum */
  aheadBehindScaleTargetRateColorOnly: {
    [processId: string]: number;
  };
  /** in ProcessGroupSwitcherDropdown, show controls to set auto-rotation between each process group on a specified interval e.g. 5sec */
  showProcessGroupSwitcherDropdownCycler: boolean;
  /** enable RangeSelector on Operator page to allow going to previous shifts or days */
  operatorPageEnableRangeSelector: boolean;
  /** in TimelineTooltip, show vertical "..." more info raw signal data API links dropdown e.g. hubletTag_0, workflow_0 */
  timelineTooltipEnableSignalValidation: boolean;
  /** custom CSS added to AppStyles */
  customCss: string;
  /** custom CSS added to AppStyles, specific to machemetry id e.g. {
                "raveng_d_001": ".timeline-tooltip-counts { display: none; };"
            } */
  customCssByProcess: { [processId: string]: string };
  /** custom CSS added to AppStyles, specific to process group name e.g. {
                "Fette Presses": ".timeline-tooltip-counts { display: none; };"
            } */
  customCssByProcessGroup: { [processGroupName: string]: string };
  /** minimum date visitable on trends page */
  trendsPageMinDateMs: number;
  /** enable group metrics on trends page */
  trendsPageEnableGroupMetrics: boolean;
  /** controls WifiBars component in Nav. 0 = disable, >0 = check internet connection every X milliseconds */
  wifiBarsUpdateIntervalMs: number;
  /** in PnL chart, if Midgard is enabled (which would normally show producing losses), hide producing losses and only show simplified "producing" bar */
  hideProducingLosses: boolean;
  /** in PnL chart, if Midgard is enabled (which would normally show quality losses), convert quality losses into producing */
  convertQualityLossToProducing: boolean;
  /** in the summary charts, we show DataDumpAggregate.pnlLosses[category].downtimeMs by default; use this to switch to showing DataDumpAggregate.pnlLosses[category].downtimePlusDependentSpeedLossMs  */
  includeDependentLosses: boolean;
  /** Whether to force bottom-up or top-down for both OEE & producing time */
  topDownOrBottomUp: TopDownOrBottomUp;
  /** hide specified process tiles e.g. ["raveng_d_001"] */
  hideProcessTiles: string[];
  /** show multiple timelines instead of just one on a grid page for a specific process e.g. ["raveng_d_002"] = "show timeline for raveng_d_002 below the normal timeline" */
  multipleTimelinesOnGridPage: string[];
  /** list of ExtraPnlColumns in ExtraPnlColumnsEnum to show in the PnLColumnChart */
  extraPnlChartColumns: ExtraPnlColumnsEnum[];
  /** {{array of category strings}} vertically shrink some categories of segments on the timeline to de-emphasize them */
  timelineShrinkSegmentCategories: string[];
  /** show links from Grid Page to Dashboard Page and back */
  gridPageLinkToDashboardPage: boolean;
  /** [intended for internal / temporary-use only] multiply the network request intervals for all resources
   * (Askr aggregates, midgard DDA etc), e.g. if we typically fetch every 30s, set this to 0.5 to fetch every 15s instead.
   */
  multiplyNetworkRequestIntervals: number;
  /** disable bigger animations that might bother some users or slow down the page */
  disableAnimations: boolean;
  /** if true, hide extra info in ProcessTile > ExpandedTileModules to preserve vertical space, for displays showing many expanded tiles */
  expandedProcessTileCompactMode: boolean;
  /** if true, activates useSetTimeZoneFromWorkscheduleConfig -> on load/reload, look in workScheduleConfig for timeZone, then set "timeZone override" setting to that timeZone */
  autoSetTimezoneOverride: boolean;
  /** pick one of "oneDay", "oneWeek" to override default trends page range of current-month  */
  trendsPageOverrideGe: false | TrendsPageStartRangesEnum;
  /** reportId used on Sight - Analytics PowerBI page */
  powerBiReportId: string;
  /** reportId used on Sight - Throughput PowerBI page */
  powerBiReportIdThroughput: string;
  /** reportId used on Sight - Logbook PowerBI page */
  powerBiReportIdLogbook: string;
  /** Array of PowerBI reports with their IDs and display names to render as links */
  powerBiReports: PowerBIReport[];
  /** updates displayed to user about latest changes to their reports - these appear as a notification badge on each associated link in LeftDrawerContent
   *
   * `{ markdown: string, mailto?: string }`
   *
   * markdown string example:
   *
   * `# Hey {{user}}, here's a new update!\n Check out the [MarkDown Cheat Sheet](https://www.markdownguide.org/cheat-sheet/)\n![A random image](https://picsum.photos/250/250)\n## Analytics page updates:\n\n- update 1\n- update 2\n\nOrdered list:\n\n1. item 1\n2. item 2\n\nLet me know what you think!
      `
   */
  powerBiUpdates: {
    /** updates for sight - logbook report */
    logbook: { markdown: string; mailto?: string };
    /** updates for sight - throughput report */
    throughput: { markdown: string; mailto?: string };
    /** updates for sight - analytics report */
    analytics: { markdown: string; mailto?: string };
  };
  /** sort order or work schedule tiles on /schedules page, using work schedule ids e.g. ["1","2"] */
  workSchedulesSortOrder: string[];
  /** Use producingTime from dataDumpAggregate, rather than calculating producingTime from effectiveProducingTime and producing losses */
  producingTimeFromDataDumpAggregate: boolean;
  /** alternative to LD flag "link to sight analytics" which allows access to Sight links in left drawer https://app.launchdarkly.com/projects/default/flags/flight-link-to-sight-analytics/targeting?env=production&selected-env=production */
  accessToSightAnalytics: boolean;
  /** initial value for dayStartMsOffset setting -- controls 24-hour mode rang, and which shifts appear as "today's shifts" -- e.g. set to -1h (-3600000) to count shift starting at 23:00-yesterday as first shift of today, or e.g. set to +6h (21600000) to count shift starting at 05:00-tomorrow as last shift of today */
  dayStartMsOffset: number | null;
  /** array of dataDumpKeys to display in TimelineTooltip > MoreInfoPanel */
  timelineTooltipAdditionalMetrics: {
    dataDumpKey: string;
    displayName: string;
    units?: string;
  }[];
};
export type TopDownOrBottomUp = "top-down" | "bottom-up" | "default";
export enum TrendsPageStartRangesEnum {
  oneDay = "oneDay",
  oneWeek = "oneWeek",
}
